/**
 * List of module information.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Coyright © Ircam Amplify 2025
 */
import { ModuleInfo, ModuleKey } from './ModuleInfo'

/**
 * The list of module information.
 */
export const ModuleInfoList: Record<ModuleKey, ModuleInfo> = {
  [ModuleKey.AiMusicDetector]: new ModuleInfo({
    serviceName: 'aidetector',
    endpoint: 'aidetector/',
    title: 'AI Music Detector',
    shortTitle: 'AiM',
    cat: 'MUSIC',
    description:
      'Detect whether a musical track has been generated by an AI model.',
  }),

  [ModuleKey.AiSpeechDetector]: new ModuleInfo({
    serviceName: 'aispeechdetector',
    endpoint: 'aispeechdetector/',
    title: 'AI Speech Detector',
    shortTitle: 'AiS',
    cat: 'VOICE',
    description: 'Verify if an audio clip is a deepfake or a voice clone.',
  }),

  [ModuleKey.StereoToSpatial]: new ModuleInfo({
    serviceName: 'naturallistening',
    endpoint: 'stereotospatial/',
    title: 'Stereo to Spatial',
    shortTitle: 'StS',
    cat: 'MUSIC',
    description:
      'Generates spatialized audio from a stereo stem file with a single API command.',
  }),

  [ModuleKey.AiReEditor]: new ModuleInfo({
    serviceName: 'aireeditor',
    endpoint: 'aireeditor/',
    title: 'AI Re-Editor',
    shortTitle: 'RE',
    cat: 'MUSIC',
    description:
      'Shorten an audio track to your needs while preserving the artistic intention.',
  }),

  [ModuleKey.StemSeparator]: new ModuleInfo({
    serviceName: 'stemseparator',
    endpoint: 'stemseparator/',
    title: 'Stem Separator',
    shortTitle: 'SS',
    cat: 'MUSIC',
    description:
      'Split a mixed track into stems (vocals, instrumental, bass, drums, and others).',
  }),

  [ModuleKey.SpeechToText]: new ModuleInfo({
    serviceName: 'speechtotext',
    endpoint: 'speechtotext/',
    title: 'Speech To Text',
    shortTitle: 'STT',
    cat: 'VOICE',
    description: 'Generates the voice transcript as a SRT and a CSV files.',
  }),

  [ModuleKey.MusicTaggerAll]: new ModuleInfo({
    serviceName: 'musicinsider',
    endpoint: 'musictagger/all',
    title: 'Music Tagger',
    shortTitle: 'MT',
    cat: 'MUSIC',
    description:
      'A music analysis tool to obtain music classification features and temporal markers.',
  }),

  [ModuleKey.MusicTaggerGenre]: new ModuleInfo({
    serviceName: 'musicinsider',
    endpoint: 'musictagger/genre',
    title: 'Genre',
    shortTitle: 'G',
    cat: 'MUSIC',
    description: 'Genre analyzer.',
  }),

  [ModuleKey.MusicTaggerMood]: new ModuleInfo({
    serviceName: 'musicinsider',
    endpoint: 'musictagger/mood',
    title: 'Mood',
    shortTitle: 'M',
    cat: 'MUSIC',
    description: 'Mood analyzer.',
  }),

  [ModuleKey.MusicTaggerInstrument]: new ModuleInfo({
    serviceName: 'musicinsider',
    endpoint: 'musictagger/instrument',
    title: 'Instrument',
    shortTitle: 'I',
    cat: 'MUSIC',
    description: 'Arrangement and instrumentation analyzer.',
  }),

  [ModuleKey.MusicTaggerRhythm]: new ModuleInfo({
    serviceName: 'musicinsider',
    endpoint: 'musictagger/rhythm',
    title: 'Rhythm',
    shortTitle: 'R',
    cat: 'MUSIC',
    description: 'Time signature and tempo analyzer.',
  }),

  [ModuleKey.MusicTaggerKey]: new ModuleInfo({
    serviceName: 'musicinsider',
    endpoint: 'musictagger/key',
    title: 'Key',
    shortTitle: 'K',
    cat: 'MUSIC',
    description: 'Determines the main key of the song.',
  }),

  [ModuleKey.MusicTaggerSummary]: new ModuleInfo({
    serviceName: 'musicinsider',
    endpoint: 'musictagger/summary',
    title: 'Summary',
    shortTitle: 'Su',
    cat: 'MUSIC',
    description:
      "Highlights the timestamp of the section representing the song's main characteristics.",
  }),

  [ModuleKey.MusicTaggerStructure]: new ModuleInfo({
    serviceName: 'musicinsider',
    endpoint: 'musictagger/structure',
    title: 'Structure',
    shortTitle: 'St',
    cat: 'MUSIC',
    description: 'Description of the overall temporal structure of a track.',
  }),

  [ModuleKey.MusicTaggerBeats]: new ModuleInfo({
    serviceName: 'musicinsider',
    endpoint: 'musictagger/beats',
    title: 'Beats',
    shortTitle: 'Bt',
    cat: 'MUSIC',
    description: 'Beats and bars analyzer.',
  }),

  [ModuleKey.MusicTaggerChords]: new ModuleInfo({
    serviceName: 'musicinsider',
    endpoint: 'musictagger/chords',
    title: 'Chords',
    shortTitle: 'Ch',
    cat: 'MUSIC',
    description: 'Provides the timestamp and value of each chord of the song.',
  }),

  [ModuleKey.LoopExtractor]: new ModuleInfo({
    serviceName: 'autoloop',
    endpoint: 'loopextractor/',
    title: 'Loop Extractor',
    shortTitle: 'LE',
    cat: 'MUSIC',
    description:
      'Extracts every available loop from an audio file, without time consuming manual operations.',
  }),

  [ModuleKey.QualityCheck]: new ModuleInfo({
    serviceName: 'qualitycheck',
    endpoint: 'qualitycheck/',
    title: 'Quality Check',
    shortTitle: 'QC',
    cat: 'MUSIC',
    description:
      'QualityCheck solution analyses the digital quality of audio files.',
  }),

  [ModuleKey.AudioTextAligner]: new ModuleInfo({
    serviceName: 'lyricsaligner',
    endpoint: 'audiotextaligner/',
    title: 'Audio-Text Aligner',
    shortTitle: 'AT',
    cat: 'VOICE',
    description:
      'Allows the user to synchronize lyrics with audio effortlessly.',
  }),

  [ModuleKey.TimePitchControl]: new ModuleInfo({
    serviceName: 'supervp',
    endpoint: 'timepitchcontrol/',
    title: 'Time-Pitch Control',
    shortTitle: 'TP',
    cat: 'MUSIC',
    description:
      'Time-Pitch Control is a tool for adjusting the duration and tone of an audio track.',
  }),

  [ModuleKey.TalkAnalyzer]: new ModuleInfo({
    serviceName: 'talkanalyzer',
    endpoint: 'talkanalyzer/',
    title: 'Talk Analyzer',
    shortTitle: 'Ta',
    cat: 'VOICE',
    description: 'Determine voice and speech characteristics.',
  }),

  [ModuleKey.CoverDetector]: new ModuleInfo({
    serviceName: 'coverdetector',
    endpoint: 'coverdetector/',
    title: 'Cover Detector',
    shortTitle: 'CD',
    cat: 'MUSIC',
    description:
      'A tool that allows the user to identify cover songs within a library of music.',
  }),

  [ModuleKey.InfringementDetector]: new ModuleInfo({
    serviceName: 'sampledetector',
    endpoint: 'infringementdetector/',
    title: 'Infringement Detector',
    shortTitle: 'ID',
    cat: 'MUSIC',
    description:
      'Detects whether a suspect track is either a copy of a reference track or not.',
  }),

  [ModuleKey.LoudnessAnalyzer]: new ModuleInfo({
    serviceName: 'loudnessanalyzer',
    endpoint: 'loudnessanalyzer/',
    title: 'Loudness Analyzer',
    shortTitle: 'LA',
    cat: 'MUSIC',
    description:
      'Provides an audio analysis report of the loudness of your file.',
  }),

  [ModuleKey.Converter]: new ModuleInfo({
    serviceName: 'converter',
    endpoint: 'converter/',
    title: 'Converter',
    shortTitle: 'C',
    cat: 'TOOLS',
    description:
      'Converts your audio file to another format, but also modify its channel configuration, sample rate or bitrate.',
  }),

  [ModuleKey.EditCut]: new ModuleInfo({
    serviceName: 'editbox',
    displayModuleName: 'Editing',
    endpoint: 'edit/cut',
    title: 'Cut',
    shortTitle: 'C',
    cat: 'TOOLS',
    description: 'Cut an audio file into two parts at a specific location.',
  }),

  [ModuleKey.EditFadeIn]: new ModuleInfo({
    serviceName: 'editbox',
    displayModuleName: 'Editing',
    endpoint: 'edit/fadein',
    title: 'Fade In',
    shortTitle: 'Fi',
    cat: 'TOOLS',
    description: 'Volume from 0% to 100% at the beginning of an audio file.',
  }),

  [ModuleKey.EditFadeOut]: new ModuleInfo({
    serviceName: 'editbox',
    displayModuleName: 'Editing',
    endpoint: 'edit/fadeout',
    title: 'Fade Out',
    shortTitle: 'Fo',
    cat: 'TOOLS',
    description: 'Volume from 100% to 0% at the end of an audio file.',
  }),

  [ModuleKey.EditMix]: new ModuleInfo({
    serviceName: 'editbox',
    displayModuleName: 'Editing',
    endpoint: 'edit/mix',
    title: 'Mix',
    shortTitle: 'Mx',
    cat: 'TOOLS',
    description: 'Superposition of two audio files.',
  }),

  [ModuleKey.EditTransition]: new ModuleInfo({
    serviceName: 'editbox',
    displayModuleName: 'Editing',
    endpoint: 'edit/transition',
    title: 'Transition',
    shortTitle: 'T',
    cat: 'TOOLS',
    description: 'Do a transition between two audio files.',
  }),

  [ModuleKey.EditMute]: new ModuleInfo({
    serviceName: 'editbox',
    displayModuleName: 'Editing',
    endpoint: 'edit/mute',
    title: 'Mute',
    shortTitle: 'Mu',
    cat: 'TOOLS',
    description: 'Mute (volume set to 0) the input audio file.',
  }),

  [ModuleKey.EditPanning]: new ModuleInfo({
    serviceName: 'editbox',
    displayModuleName: 'Editing',
    endpoint: 'edit/panning',
    title: 'Panning',
    shortTitle: 'P',
    cat: 'TOOLS',
    description: 'Adjusts the stereo balance of an audio signal.',
  }),
}

/**
 * Return a module information given its URL path segment.
 *
 * @param segment The URL path segment.
 * @returns The ModuleConfig instance or undefined.
 */
export function getModuleInfoByUrlPathSegment(
  segment: string
): ModuleInfo | undefined {
  return Object.values(ModuleInfoList).find(
    (info) => info.urlPathSegment === segment
  )
}
